import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ENV from '@/config/env';
import { ChevronDown, LogOut, Search, User, Shield, Brain, Users, Settings, Focus, Info, Mail, Bookmark } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@dashai/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dashai/components/ui/dropdown-menu';
import { Switch } from '@dashai/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@dashai/components/ui/popover';
import { WorkspacePicker } from './WorkspacePicker';
import { cn, formatClientName } from '@dashai/lib/utils';

interface UserProfileDropdownProps {
  userName: string;
  userEmail: string;
  userPhotoUrl?: string;
  // The current workspace's company logo. The top-bar avatar shows it ahead of
  // the photo; the sidebar avatar ignores it.
  workspaceLogoUrl?: string;
  allowedClients: string[];
  // The list failed to load: the switcher says so and offers a retry.
  workspaceListError?: boolean;
  workspaceListLoaded?: boolean;
  onRetryWorkspaceList?: () => void;
  currentClient: string | null;
  isCollapsed?: boolean;
  // Where the menu is anchored. The sidebar footer opens it upward from an
  // avatar row; the navy top bar opens it downward from a white-on-navy
  // avatar, workspace and email block.
  trigger?: 'sidebar' | 'topbar';
  // Rendered under the name and email. The top bar puts the role summary here,
  // because it has no sidebar to show the role card in.
  header?: React.ReactNode;
  isAdmin?: boolean;
  // Staff-only switcher controls: workspace pinning and the presenting toggle.
  isStaff?: boolean;
  // Whether the User Management / Settings nav items are shown. Computed by the
  // parent from the same predicate as the route guard, so the menu never offers
  // a link that redirects to "/".
  canAccessUserManagement?: boolean;
  pinnedWorkspaces?: string[];
  focusMode?: boolean;
  onTogglePinWorkspace?: (client: string) => void;
  onToggleFocusMode?: () => void;
  onClientChange: (client: string) => void;
  onAccountProfile?: () => void;
  onProfileMemory?: () => void;
  onSavedCanvas?: () => void;
  onScheduledReports?: () => void;
  onAdminPanel?: () => void;
  onSignOut: () => void;
}

export function UserProfileDropdown({
  userName,
  userEmail,
  userPhotoUrl,
  workspaceLogoUrl,
  allowedClients,
  workspaceListError = false,
  workspaceListLoaded = true,
  onRetryWorkspaceList,
  currentClient,
  isCollapsed = false,
  trigger = 'sidebar',
  header,
  isAdmin = false,
  isStaff = false,
  canAccessUserManagement = false,
  pinnedWorkspaces = [],
  focusMode = false,
  onTogglePinWorkspace,
  onToggleFocusMode,
  onClientChange,
  onAccountProfile,
  onProfileMemory,
  onSavedCanvas,
  onScheduledReports,
  onAdminPanel,
  onSignOut,
}: UserProfileDropdownProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const accountRef = useRef<HTMLButtonElement>(null);
  const openingPicker = useRef(false);
  const showWorkspaceSelector = workspaceListError || !workspaceListLoaded || allowedClients.length !== 1;

  // Get initials from name or email
  const getInitials = () => {
    if (userName) {
      const parts = userName.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return userName.slice(0, 2).toUpperCase();
    }
    if (userEmail) {
      return userEmail.slice(0, 2).toUpperCase();
    }
    return 'U';
  };


  const isTopBar = trigger === 'topbar';
  const showAdminPanel = isAdmin && !!onAdminPanel;
  const hasUtilityItems = canAccessUserManagement || showAdminPanel;
  // A logo that fails to load gives way to the photo, then the initials.
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  const showLogo = isTopBar && !!workspaceLogoUrl && failedLogoUrl !== workspaceLogoUrl;

  return (
    <>
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        {isTopBar ? (
          <button ref={accountRef} className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-topbar-foreground/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-topbar-foreground/40">
            {/* The accessible name starts with the visible workspace and email,
                so voice control can target the button by what it shows. The
                hidden suffix names what the button opens. */}
            <Avatar aria-hidden className="h-10 w-10 shrink-0">
              {showLogo ? (
                // The logo sits on a white disc with a hairline ring, so a
                // dark or transparent mark still reads on the navy bar.
                <AvatarImage
                  key={workspaceLogoUrl}
                  src={workspaceLogoUrl}
                  alt=""
                  data-testid="workspace-logo"
                  className="rounded-full bg-white object-contain p-[5px] shadow-[inset_0_0_0_1px_rgba(12,16,20,0.12)]"
                  onLoadingStatusChange={(status) => {
                    if (status === 'error') setFailedLogoUrl(workspaceLogoUrl);
                  }}
                />
              ) : (
                <AvatarImage src={userPhotoUrl} alt={userName} />
              )}
              <AvatarFallback className="bg-topbar-accent text-topbar-foreground text-sm font-semibold">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="hidden min-w-0 text-left lg:block">
              <p className="max-w-[180px] truncate text-sm font-medium text-topbar-foreground">
                {currentClient ? formatClientName(currentClient) : 'Workspace'}
              </p>
              <p className="max-w-[180px] truncate text-xs text-topbar-foreground/60">{userEmail || 'User'}</p>
            </div>
            <span className="sr-only">Account menu</span>
            <ChevronDown className="h-4 w-4 shrink-0 text-topbar-foreground/60" />
          </button>
        ) : (
          <button
            ref={accountRef}
            className={cn(
              "flex items-center gap-3 w-full rounded-lg transition-colors",
              "hover:bg-sidebar-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-border",
              isCollapsed ? "justify-center p-2" : "px-3 py-2"
            )}
          >
            <Avatar className="h-9 w-9 shrink-0">
              <AvatarImage src={userPhotoUrl} alt={userName} />
              <AvatarFallback className="bg-[#5C6BC0] text-white text-sm font-semibold">
                {getInitials()}
              </AvatarFallback>
            </Avatar>

            {!isCollapsed && (
              <>
                <div className="flex-1 text-left min-w-0">
                  {currentClient && <p className="truncate text-sm font-medium text-sidebar-foreground">{formatClientName(currentClient)}</p>}
                  <p className="text-sm font-medium text-sidebar-foreground truncate">
                    {userEmail || 'User'}
                  </p>
                </div>
                <ChevronDown className="h-4 w-4 text-sidebar-foreground/60 shrink-0" />
              </>
            )}
            <span className="sr-only">{isCollapsed && currentClient ? `${formatClientName(currentClient)} ` : ''}Account menu</span>
          </button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={isTopBar ? "end" : isCollapsed ? "start" : "end"}
        side={isTopBar ? "bottom" : "top"}
        className={cn('w-64 bg-popover border border-border z-50', isTopBar && 'rounded-xl p-1.5 shadow-lg')}
        sideOffset={isTopBar ? 10 : 8}
        onCloseAutoFocus={(event) => {
          if (openingPicker.current) {
            event.preventDefault();
            openingPicker.current = false;
          }
        }}
      >
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">{userName || 'User'}</p>
            <p className="text-xs text-muted-foreground">{userEmail}</p>
            {!showWorkspaceSelector && currentClient && <p className="text-sm font-medium">{formatClientName(currentClient)}</p>}
          </div>
        </DropdownMenuLabel>

        {header}

        <DropdownMenuSeparator />

        {showWorkspaceSelector && (
          <>
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={() => {
                openingPicker.current = true;
                setMenuOpen(false);
                setPickerOpen(true);
              }}
            >
              <Search aria-hidden="true" className="mr-2 h-4 w-4" />
              Workspace Selector
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {isStaff && (
          <>
            <div className="px-2 py-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1 text-sm">
                  <Focus aria-hidden="true" className="mr-1 h-4 w-4" />Presenting
                  <Popover>
                    <PopoverTrigger asChild>
                      <DropdownMenuItem asChild role="button" className="p-1" onSelect={(event) => event.preventDefault()}>
                        <button type="button" aria-label="About Presenting" className="rounded text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring">
                          <Info aria-hidden="true" className="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuItem>
                    </PopoverTrigger>
                    <PopoverContent aria-label="About Presenting" side="left" align="start" className="z-[60] w-60 text-sm" onOpenAutoFocus={(event) => event.preventDefault()}>
                      Hide other workspace names while sharing your screen.
                    </PopoverContent>
                  </Popover>
                </div>
                <DropdownMenuItem asChild role="switch" className="rounded-full p-0 focus:bg-input data-[state=checked]:focus:bg-primary" onSelect={(event) => event.preventDefault()}>
                  <Switch aria-label="Presenting" checked={focusMode} onCheckedChange={() => onToggleFocusMode?.()} />
                </DropdownMenuItem>
              </div>
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Account Profile */}
        {onAccountProfile && (
          <DropdownMenuItem
            onClick={onAccountProfile}
            className="cursor-pointer"
          >
            <User className="mr-2 h-4 w-4" />
            Account Profile
          </DropdownMenuItem>
        )}

        {/* Saved Canvas — moved here from the sidebar's DashHub section;
            same treatment as Scheduled Reports below */}
        {onSavedCanvas && (
          <DropdownMenuItem onClick={onSavedCanvas} className="cursor-pointer">
            <Bookmark className="mr-2 h-4 w-4" />
            Saved Canvas
          </DropdownMenuItem>
        )}

        {/* Scheduled Reports — lives here rather than in the role-filtered
            sidebar */}
        {onScheduledReports && (
          <DropdownMenuItem
            onClick={onScheduledReports}
            className="cursor-pointer"
          >
            <Mail className="mr-2 h-4 w-4" />
            Scheduled Reports
          </DropdownMenuItem>
        )}

        {/* Profile Memory */}
        {onProfileMemory && (
          <DropdownMenuItem
            onClick={onProfileMemory}
            className="cursor-pointer"
          >
            <Brain className="mr-2 h-4 w-4" />
            Profile Memory
          </DropdownMenuItem>
        )}

        {hasUtilityItems && <DropdownMenuSeparator />}

        {/* Utility Pages — User Management + Settings are gated to users who
            can actually reach those pages (same predicate as the route guard). */}
        {canAccessUserManagement && (
          <>
            <DropdownMenuItem
              onClick={() => navigate('/user_management')}
              className="cursor-pointer"
            >
              <Users className="mr-2 h-4 w-4" />
              User Management
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate('/settings')}
              className="cursor-pointer"
            >
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
          </>
        )}

        {/* Admin Panel - only visible to admins */}
        {showAdminPanel && (
          <DropdownMenuItem
            onClick={onAdminPanel}
            className="cursor-pointer"
          >
            <Shield className="mr-2 h-4 w-4" />
            Admin Panel
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* Log Out */}
        <DropdownMenuItem
          onClick={onSignOut}
          className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
      <WorkspacePicker
        open={pickerOpen}
        allowedClients={allowedClients}
        currentClient={currentClient}
        pinnedWorkspaces={pinnedWorkspaces}
        isStaff={isStaff}
        focusMode={focusMode}
        workspaceListLoaded={workspaceListLoaded}
        workspaceListError={workspaceListError}
        onRetryWorkspaceList={onRetryWorkspaceList}
        onTogglePinWorkspace={onTogglePinWorkspace}
        onClientChange={onClientChange}
        onClose={() => setPickerOpen(false)}
        returnFocusRef={accountRef}
      />
    </>
  );
}
